import * as React from 'react';

import {
  enumRoleSortOption,
  type RoleSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  Checkbox,
  Col,
  FloatingToolbar,
  IconButton,
  Pagination,
  Row,
  SearchField,
  TinyTable,
  type PaginationProps,
  type TinyTableSorting,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import {
  LucideArchive,
  LucideHash,
  LucidePlusCircle,
  LucideRotateCcw,
  LucideType,
  LucideX,
} from 'lucide-react';

import { RoleActionConfirmationDialog } from '../_shared/RoleActionConfirmationDialog';

import { useRolesQuery } from './_graphql/useRolesQuery';
import { useRoleUserCountsQuery } from './_graphql/useRoleUserCountsQuery';
import { useRoleActionDialog } from './_hooks/useRoleActionDialog';
import { AddRoleDialog } from './AddRoleDialog';
import { RoleRecord } from './types';

import {
  RoleStatusBadge,
  SettingsContent,
} from '~/modules/settings/_components/_shared';
import { $openAddRoleDialog } from '~/modules/settings/_state/addRoleDialog.state';
import { useSettingsLocation } from '~/modules/settings/_state/settingsModule.state';
import * as state from '~/modules/settings/_state/teamPermissions.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';
import { PermissionGate } from '~/shared/components/PermissionGate';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const RolesTabView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const openAddRoleDialog = useSetAtom($openAddRoleDialog);

  const [selectedRoleIds, setSelectedRoleIds] = useAtom(state.$selectedRoleIds);
  const [showArchived, setShowArchived] = useAtom(state.$showArchivedRoles);
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [offset, setOffset] = useAtom(state.$rolesOffset);
  const [limit, setLimit] = useAtom(state.$rolesLimit);
  const [sorting, setSorting] = React.useState<TinyTableSorting>({
    column: 'name',
    direction: 'asc',
  });

  const serverSorting = React.useMemo((): RoleSortOption[] => {
    if (sorting.column === 'name') {
      return [
        sorting.direction === 'asc'
          ? enumRoleSortOption.NameAsc
          : enumRoleSortOption.NameDesc,
      ];
    }
    return [enumRoleSortOption.NameAsc];
  }, [sorting]);

  const roleActionDialog = useRoleActionDialog({
    onSuccess: () => setSelectedRoleIds([]),
  });

  const {
    data: { roles: rolesData = [], total = 0 } = { roles: [], total: 0 },
    isLoading,
    error,
  } = useRolesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    sorting: serverSorting,
    includeArchived: showArchived,
    limit,
    offset,
  });

  const { data: userCountsData } = useRoleUserCountsQuery({});

  const roles = React.useMemo(() => {
    const counts = userCountsData?.counts ?? {};
    const merged = rolesData.map((role) => ({
      ...role,
      userCount: counts[role.id] ?? 0,
    }));

    if (sorting.column === 'name') return merged;

    return [...merged].sort((a, b) => {
      const dir = sorting.direction === 'asc' ? 1 : -1;
      switch (sorting.column) {
        case 'userCount':
          return dir * (a.userCount - b.userCount);
        case 'status':
          return dir * a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
  }, [rolesData, userCountsData, sorting]);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setOffset(0);
  };

  const handleSortChange = (newSorting: TinyTableSorting) => {
    setSorting(newSorting);
    if (newSorting.column === 'name') {
      setOffset(0);
    }
  };

  const handleRowClick = (role: RoleRecord) => {
    setLocation(`/team-and-permissions/roles/${role.id}` as never);
  };

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedRoleIds),
    [selectedRoleIds],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedRoleIds(Array.from(keys) as string[]);
    },
    [setSelectedRoleIds],
  );

  const { activeRoleIds, archivedRoleIds } = React.useMemo(() => {
    const active: string[] = [];
    const archived: string[] = [];
    for (const id of selectedRoleIds) {
      const role = roles.find((r) => r.id === id);
      if (role?.status === 'archived') {
        archived.push(id);
      } else if (role) {
        active.push(id);
      }
    }
    return { activeRoleIds: active, archivedRoleIds: archived };
  }, [selectedRoleIds, roles]);

  return (
    <>
      <SettingsContent className="@RolesTabView h-full">
        <Col className="h-full gap-4">
          <Row gap2 className="items-center">
            <SearchField
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={t.teamAndPermissions.search.placeholder}
              autoFocus
            />
            <Checkbox
              isSelected={showArchived}
              onChange={setShowArchived}
              className="text-sm whitespace-nowrap text-neutral/70"
            >
              {t.teamAndPermissions.roles.table.showArchived}
            </Checkbox>
            <PermissionGate action="roleCreate">
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={openAddRoleDialog}
              >
                {t.teamAndPermissions.actions.addRole}
              </Button>
            </PermissionGate>
          </Row>

          <TinyTable<RoleRecord>
            rowKey={(row) => row.id}
            selectable
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            isLoading={isLoading}
            error={error ?? undefined}
            sorting={sorting}
            onSortChange={handleSortChange}
            onRowClick={handleRowClick}
            columns={[
              {
                key: 'name',
                label: t.teamAndPermissions.roles.table.columns.name,
                icon: LucideType,
                sortable: true,
                cell: (row) => (
                  <span
                    className={row.status === 'archived' ? 'line-through' : ''}
                  >
                    {row.name}
                  </span>
                ),
              },
              {
                key: 'userCount',
                label: t.teamAndPermissions.roles.table.columns.users,
                icon: LucideHash,
                width: 100,
                sortable: true,
                cell: (row) => row.userCount,
              },
              {
                key: 'status',
                label: t.teamAndPermissions.roles.table.columns.status,
                width: 120,
                sortable: true,
                cell: (row) => <RoleStatusBadge status={row.status} />,
              },
              {
                key: 'actions',
                label: '',
                width: 60,
                preventRowClick: true,
                cell: (row) => (
                  <PermissionGate action="roleUpdate">
                    <IconButton
                      variant="outline"
                      icon={
                        row.status === 'archived' ? (
                          <LucideRotateCcw size={16} />
                        ) : (
                          <LucideArchive size={16} />
                        )
                      }
                      onPress={() => roleActionDialog.openForRole(row)}
                      isDisabled={roleActionDialog.isPending}
                      aria-label={
                        row.status === 'archived'
                          ? t.teamAndPermissions.roles.table.actions.restoreRole
                          : t.teamAndPermissions.roles.table.actions.archiveRole
                      }
                    />
                  </PermissionGate>
                ),
              },
            ]}
            data={roles}
          />

          <FloatingToolbar visible={selectedRoleIds.length > 0}>
            <div className="flex flex-row items-center justify-between gap-3">
              <div className="flex flex-row items-center gap-2 text-sm">
                <Badge color="primary" size="small">
                  {selectedRoleIds.length}
                </Badge>{' '}
                {t.teamAndPermissions.roles.bulk.selected}
              </div>
              <div className="flex flex-row items-center gap-2">
                <PermissionGate action="roleUpdate">
                  {activeRoleIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="small"
                      icon={<LucideArchive className="size-4" />}
                      onPress={() =>
                        roleActionDialog.openBulkArchive(activeRoleIds)
                      }
                      isDisabled={roleActionDialog.isPending}
                    >
                      {t.teamAndPermissions.roles.bulk.archive} (
                      {activeRoleIds.length})
                    </Button>
                  )}
                  {archivedRoleIds.length > 0 && (
                    <Button
                      variant="outline"
                      size="small"
                      icon={<LucideRotateCcw className="size-4" />}
                      onPress={() =>
                        roleActionDialog.openBulkRestore(archivedRoleIds)
                      }
                      isDisabled={roleActionDialog.isPending}
                    >
                      {t.teamAndPermissions.roles.bulk.restore} (
                      {archivedRoleIds.length})
                    </Button>
                  )}
                </PermissionGate>
                <LucideX
                  className="size-4 cursor-pointer text-neutral/80"
                  onClick={() => setSelectedRoleIds([])}
                />
              </div>
            </div>
          </FloatingToolbar>

          {total > 0 && (
            <Row className="justify-end">
              <Pagination
                limit={limit}
                offset={offset}
                total={total}
                onChange={handlePaginationChange}
              />
            </Row>
          )}
        </Col>
      </SettingsContent>

      <AddRoleDialog />

      <RoleActionConfirmationDialog
        dialogState={roleActionDialog.dialogState}
        dialogs={t.teamAndPermissions.roles.dialogs}
        isPending={roleActionDialog.isPending}
        onConfirm={roleActionDialog.confirm}
        onCancel={roleActionDialog.cancel}
        onOpenChange={roleActionDialog.handleOpenChange}
        className="@RolesTabViewRoleActionDialog"
      />
    </>
  );
};
